import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Users, MessageCircle, BookMarked, DollarSign, Sparkles, Eye, Target, Wind, Orbit } from "lucide-react";
import { Starfield } from "../components/Starfield";
import { Header } from "../components/Header";
import { api } from "../lib/api";
import { Switch } from "../components/ui/switch";
import { PromoAdminCard } from "../components/PromoAdminCard";
import { toast } from "sonner";

const MODE_META = {
  reflect: { label: "Reflect", icon: Eye },
  belief_clearing: { label: "Clear a Belief", icon: Sparkles },
  intention: { label: "Set an Intention", icon: Target },
  manifest: { label: "Manifest", icon: Orbit },
  quiet_minute: { label: "Quiet Minute", icon: Wind },
};
const PLAN_LABEL = { free: "Seeker", ascendent: "Ascendent", luminous: "Luminous" };

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [s, u] = await Promise.all([api.get("/admin/stats"), api.get("/admin/users")]);
        setStats(s.data);
        setUsers(u.data);
      } catch {}
    };
    load();
  }, []);

  const togglePuter = async (u, enabled) => {
    try {
      await api.patch(`/admin/users/${u.user_id}/puter`, { enabled });
      setUsers((list) => list.map((x) => (x.user_id === u.user_id ? { ...x, puter_enabled: enabled, use_puter: enabled } : x)));
      toast.success(`${u.email} now uses ${enabled ? "Puter free AI" : "app AI credits"}`);
    } catch {
      toast.error("Couldn't update this member");
    }
  };

  const cards = stats
    ? [
        { icon: Users, label: "Total members", value: stats.total_users, sub: `+${stats.new_users_week} this week` },
        { icon: MessageCircle, label: "Sessions", value: stats.total_sessions, sub: `${stats.total_messages} messages` },
        { icon: BookMarked, label: "Insights saved", value: stats.total_journal, sub: "across all members" },
        { icon: DollarSign, label: "Revenue", value: `$${stats.revenue_usd}`, sub: `${stats.paid_subscriptions} paid subs` },
      ]
    : [];

  return (
    <div className="relative min-h-screen bg-cg-bg">
      <Starfield />
      <Header />
      <section className="relative z-10 max-w-6xl mx-auto px-5 pt-32 pb-24">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-cg-gold mb-3">Mission control</p>
        <h1 className="font-serif text-3xl sm:text-4xl font-extralight tracking-tight mb-10">Admin dashboard</h1>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
          {cards.map((c, i) => (
            <motion.div
              key={c.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className="rounded-2xl p-6 bg-cg-card border border-[rgba(140,79,214,0.22)]"
              data-testid={`admin-stat-${c.label.toLowerCase().split(" ")[0]}`}
            >
              <c.icon className="h-5 w-5 text-cg-cyan mb-3" />
              <p className="font-serif text-3xl text-cg-textp">{c.value}</p>
              <p className="text-sm text-cg-textp mt-1">{c.label}</p>
              <p className="text-xs text-cg-textm mt-0.5">{c.sub}</p>
            </motion.div>
          ))}
        </div>

        {stats && (
          <div className="grid md:grid-cols-2 gap-5 mb-10">
            <div className="rounded-2xl p-6 bg-cg-card border border-[rgba(140,79,214,0.22)]">
              <h2 className="font-serif text-lg text-cg-textp mb-4">Members by plan</h2>
              <div className="space-y-3">
                {Object.entries(stats.plans).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between text-sm">
                    <span className="text-cg-texts">{PLAN_LABEL[k] || k}</span>
                    <span className="font-mono text-cg-cyan">{v}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-2xl p-6 bg-cg-card border border-[rgba(140,79,214,0.22)]">
              <h2 className="font-serif text-lg text-cg-textp mb-4">Sessions by portal</h2>
              <div className="space-y-3">
                {Object.entries(MODE_META).map(([k, meta]) => (
                  <div key={k} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-cg-texts"><meta.icon className="h-4 w-4 text-cg-cyan" />{meta.label}</span>
                    <span className="font-mono text-cg-cyan">{stats.mode_breakdown?.[k] || 0}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <PromoAdminCard />

        <div className="rounded-2xl bg-cg-card border border-[rgba(140,79,214,0.22)] overflow-hidden">
          <h2 className="font-serif text-lg text-cg-textp p-6 pb-4">Members ({users.length})</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-users-table">
              <thead>
                <tr className="text-left text-cg-textm border-y border-[rgba(140,79,214,0.18)]">
                  <th className="px-6 py-3 font-medium">Name</th>
                  <th className="px-6 py-3 font-medium">Email</th>
                  <th className="px-6 py-3 font-medium">Plan</th>
                  <th className="px-6 py-3 font-medium">Auth</th>
                  <th className="px-6 py-3 font-medium">Sessions</th>
                  <th className="px-6 py-3 font-medium">Puter AI</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id} className="border-b border-[rgba(140,79,214,0.1)] hover:bg-[rgba(140,79,214,0.06)]">
                    <td className="px-6 py-3 text-cg-textp">{u.name}{u.role === "admin" && <span className="ml-2 text-[10px] font-mono uppercase text-cg-gold">admin</span>}</td>
                    <td className="px-6 py-3 text-cg-texts">{u.email}</td>
                    <td className="px-6 py-3"><span className="text-xs font-mono uppercase tracking-wide text-cg-cyan">{PLAN_LABEL[u.plan] || u.plan}</span></td>
                    <td className="px-6 py-3 text-cg-textm">{u.auth_provider}</td>
                    <td className="px-6 py-3 font-mono text-cg-texts">{u.session_count}</td>
                    <td className="px-6 py-3">
                      {u.is_core_puter ? (
                        <span className="text-[10px] font-mono uppercase tracking-widest text-cg-gold" data-testid={`puter-always-${u.user_id}`}>Always on</span>
                      ) : (
                        <Switch checked={!!u.puter_enabled} onCheckedChange={(v) => togglePuter(u, v)} data-testid={`puter-toggle-${u.user_id}`} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}
